/**
 * The merge, sort and release gate behind "holidays in Coming up"
 * (THE-746 Phase 2, PR 2.4 / THE-760).
 *
 * Why a separate module rather than more code inside ComingUpStrip.tsx:
 *
 *  - ComingUpStrip.tsx pulls Firebase at module top (via
 *    `useMaterializedComingUp`), so anything that can be tested without
 *    rendering lives beside it instead of in it — the same split as
 *    `calendar/holidayEvents.ts`, `calendar/personEvents.ts` and
 *    `calendar/initialScrollAnchor.ts`.
 *  - `ComingUpStripStyleContract.test.ts` reads ComingUpStrip.tsx as TEXT
 *    and pins PR 2.3's geometry out of it. Keeping the holiday logic out of
 *    that file keeps this PR's diff away from a fitness test it does not own.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT DO: decode `nextEventAt`. The
 * person side of the strip already decodes it (Firestore Timestamp |
 * `{seconds}` | string) and already computes `days` via
 * `ComingUpStrip.daysFromToday`. Importing that back here would make
 * ComingUpStrip ⇄ comingUpEntries a true import cycle, and moving it out of
 * ComingUpStrip.tsx would break the PR 2.3 source assertions above. So the
 * component hands person entries in already-normalized, and this module owns
 * the holiday side plus the one thing neither side can own alone: the order.
 */
import { holidayEmoji, holidaysInRange } from '../calendar/holidays';
import type { HolidayEntry } from '../calendar/holidays';
import { filterHiddenHolidays } from '../holidayPrefs/hiddenHolidays';
import type { ComingUpEvent } from '../schemas/materializedComingUp';

const MS_PER_DAY = 86_400_000;

/**
 * 🔒 THE PHASE 3 RELEASE GATE.
 *
 * THE-746's ground rule: "no holidays appear in Coming up or on the calendar
 * until Phase 3's saved setting has shipped." Phase 3's setting is
 * `hiddenHolidays` on `theaWebUser` (THE-761, PR 3.1) and it is not live —
 * THE-762 is the human-gated deploy that makes the `updateHolidayPrefs`
 * callable answer, and THE-764 (PR 3.3) is the PR that wires the saved list
 * into this surface and the calendar.
 *
 * So PR 2.4 ships the merge, the sort and the hidden-list filter, and ships
 * them OFF. This constant is the whole door, and it is a CONSTANT on purpose
 * rather than a third Firestore feature-flag resolver:
 *
 *  - The thing it waits on is a backend DEPLOY (THE-762), and the PR that
 *    opens it (THE-764) is a thea-web code change that has to be built and
 *    released anyway. A deploy-free remote switch buys nothing here, and it
 *    would need a `featureFlags` rules block that does not exist (see the
 *    FAIL-CLOSED note in `freshFind/freshFindFridaysFlag.ts`).
 *  - A constant is provably closed by reading one line, which is what the
 *    acceptance criterion "prove no holiday entries are exposed before the
 *    release gate opens" actually asks for. A remote flag can only be
 *    proven closed for the state the test happens to mock.
 *
 * OPEN as of THE-764 (PR 3.3), together with its calendar sibling
 * (`HOLIDAYS_RELEASED_ON_CALENDAR`) and in the same change that supplies the
 * real `hiddenHolidays` list. The preconditions above are all met: THE-761
 * shipped the field and the callable, THE-762 deployed it — the backend deploy
 * this constant was really waiting on — THE-763 shipped the Holidays settings
 * page, THE-765 made the GA4 payload generic, and THE-764 wired the saved list
 * into both surfaces along with its `ready` flag.
 *
 * The two doors open together on purpose. Opening one alone reintroduces the
 * ground-rule breach on the other surface, which is the bug THE-780 found.
 */
export const HOLIDAYS_RELEASED_IN_COMING_UP = true;

/** A holiday entry resolved to a concrete date, as `holidaysInRange` returns. */
export type DatedHoliday = HolidayEntry & { date: string };

/** A materialized person event (birthday / anniversary), normalized by the
 *  caller because only the caller can decode `nextEventAt`. */
export interface ComingUpPersonEntry {
  kind: 'person';
  /** The materialized entry, untouched. */
  event: ComingUpEvent;
  /** The decoded `nextEventAt`, so the card can build its deep link. */
  when: Date;
  /** Sort key — the event's own calendar date, `YYYY-MM-DD`. */
  dayIso: string;
  /** Whole days from the viewer's LOCAL today. */
  days: number;
}

/** A holiday occurrence, normalized by this module. */
export interface ComingUpHolidayEntry {
  kind: 'holiday';
  holiday: DatedHoliday;
  /**
   * The card's display glyph — 🎃 for Halloween (THE-793 item 2).
   *
   * Carried ON THE ENTRY rather than looked up in the component, because the
   * component is forbidden from importing `calendar/holidays` at all: a direct
   * import is a path around the Phase 3 release gate, pinned by BOTH
   * `comingUpEntries.releaseGate.test.ts` and `ComingUpStripStyleContract.test.ts`.
   * So the glyph comes through the gate with the rest of the holiday — it can
   * only exist on an entry the gate already let out.
   */
  emoji: string;
  /** Sort key — the holiday's date, `YYYY-MM-DD`. Same field as `holiday.date`;
   *  named `dayIso` so both arms of the union sort on one key. */
  dayIso: string;
  /** Whole days from the viewer's LOCAL today. */
  days: number;
}

export type ComingUpEntry = ComingUpPersonEntry | ComingUpHolidayEntry;

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The calendar date a materialized event falls on, `YYYY-MM-DD`.
 *
 * Reads on EXACTLY the same basis as `ComingUpStrip.daysFromToday` and
 * `ComingUpStrip.eventTileDate`: `eventDateIso` when the BE provided a
 * well-formed one, else the timestamp's **UTC** fields — because the BE
 * encodes the date as midnight UTC and it is a date, not a moment. Local
 * fields here would place every event west of UTC on the previous day after
 * ~5pm, which on a sort key means a birthday jumping ahead of a holiday it
 * actually follows.
 *
 * Pinned against both of those functions in `comingUpEntries.test.ts` rather
 * than trusted to stay in step by comment.
 */
export function eventDayIso(nextEventAt: Date, eventDateIso?: string): string {
  const m = eventDateIso ? DAY_RE.exec(eventDateIso) : null;
  if (m) {
    const monthIdx = Number(m[2]) - 1;
    const day = Number(m[3]);
    // A well-formed-but-impossible iso ("2026-13-40") falls through to the
    // timestamp, same policy as eventTileDate.
    if (monthIdx >= 0 && monthIdx <= 11 && day >= 1 && day <= 31) return eventDateIso!;
  }
  const y = nextEventAt.getUTCFullYear();
  const mo = String(nextEventAt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(nextEventAt.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/**
 * Whole-day diff between a `YYYY-MM-DD` calendar date and the viewer's LOCAL
 * calendar date.
 *
 * The holiday-side twin of `ComingUpStrip.daysFromToday`, and asserted
 * equivalent to it in `comingUpEntries.test.ts`. Two different calendars on
 * purpose (the 2026-07-10 "Ashley at 7:23pm" bug): the event side is a bare
 * calendar date, the `now` side reads LOCAL fields because "today" means today
 * where the viewer is standing.
 *
 * Returns NaN for a malformed date, which `holidayEntriesForComingUp` filters
 * out — a holiday whose `dateFor` ever returns junk must not render as
 * "In NaN days".
 */
export function daysFromIsoDate(dayIso: string, now: Date): number {
  const m = DAY_RE.exec(dayIso);
  if (!m) return NaN;
  const eventCal = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const todayCal = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((eventCal - todayCal) / MS_PER_DAY);
}

export interface HolidayEntriesOptions {
  /** The viewer's clock. */
  now: Date;
  /** The strip's visible window, in days. The caller passes its own
   *  `LOOK_AHEAD_DAYS` so the two arms share one window by construction. */
  lookAheadDays: number;
  /**
   * The user's SAVED hidden-holiday list — `hiddenHolidays` on `theaWebUser`
   * (THE-761). Holiday `eventType` strings.
   *
   * EMPTY MEANS ALL HOLIDAYS ON, and `undefined` — the absent-field case, which
   * is every user until she opens the Holidays page — means the same. Get this
   * backwards and every user sees zero holidays. It is a HIDDEN list, never a
   * shown list; there is deliberately no "show all" sentinel to misread.
   *
   * Typed `unknown` as of PR 3.3: the value arrives from a Firestore document
   * rather than from the type system, and the wrong shape must fail safe rather
   * than throw inside a render. See `holidayPrefs/hiddenHolidays.ts`.
   */
  hiddenHolidays?: unknown;
  /**
   * Has the saved list actually ARRIVED? `useHolidayPrefs().ready`.
   *
   * Defaults to `true` so PR 2.4's existing call sites — which pass a concrete
   * list and are asking a question about the list, not about its arrival — read
   * unchanged. That default is only safe because it sits BEHIND the release
   * gate: while `released` is false no holiday renders at all, so a caller who
   * forgets `ready` cannot leak anything today. The production caller that
   * opens the gate must pass the real flag, and PR 3.3's companion assertion in
   * `comingUpEntries.releaseGate.test.ts` is what holds it to that.
   *
   * `false` means NOTHING renders: the pre-snapshot `[]` from the hook is
   * indistinguishable from a genuine "she has hidden nothing", and guessing
   * all-on there flashes holidays she went out of her way to hide.
   */
  ready?: boolean;
  /**
   * Override the Phase 3 release gate. Tests and stories pass `true` to
   * exercise the merged strip; PRODUCTION CALLERS MUST NOT PASS THIS — it
   * defaults to `HOLIDAYS_RELEASED_IN_COMING_UP`, and
   * `comingUpEntries.releaseGate.test.ts` asserts the production caller
   * (`LandingPage.tsx`) never supplies it.
   */
  released?: boolean;
}

/**
 * The holiday arm of the strip: every holiday inside the visible window that
 * the user has not hidden, normalized for merging.
 *
 * GATE FIRST, BEFORE ANY WORK. When the release gate is closed this returns a
 * frozen empty array without consulting `holidaysInRange` at all, so there is
 * no half-built holiday list for a later refactor to accidentally reach past
 * the gate and render. The `ready` check sits immediately behind it for the
 * same reason — both "no holidays may render" answers are given before the
 * holiday table is touched.
 *
 * The hidden-list filter itself is NOT written here: it is
 * `holidayPrefs/filterHiddenHolidays`, shared with the calendar surface so one
 * rule serves both (PR 3.3 / THE-764).
 */
export function holidayEntriesForComingUp(
  options: HolidayEntriesOptions,
): ComingUpHolidayEntry[] {
  const {
    now,
    lookAheadDays,
    hiddenHolidays,
    ready = true,
    released = HOLIDAYS_RELEASED_IN_COMING_UP,
  } = options;

  if (!released) return [];
  if (!ready) return [];

  // `holidaysInRange` reads LOCAL Y/M/D off both bounds, so build both bounds
  // in local time — the viewer's today through the end of her window.
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + lookAheadDays);

  return filterHiddenHolidays(holidaysInRange(now, to), { ready, hiddenHolidays })
    .map((h) => ({
      kind: 'holiday' as const,
      holiday: h,
      // `holidayEmoji` is total over `HolidayEventType` and `h.eventType` is
      // one by construction, so the `?? ''` is unreachable — it exists only
      // because the helper's signature takes a `string` (it also serves
      // person-typed call sites, which must get undefined).
      emoji: holidayEmoji(h.eventType) ?? '',
      dayIso: h.date,
      days: daysFromIsoDate(h.date, now),
    }))
    // `holidaysInRange` already bounds the window, but a NaN date (a broken
    // `dateFor`) would slip through its string compare. Re-assert here so the
    // strip can never render "In NaN days".
    .filter((e) => Number.isFinite(e.days) && e.days >= 0 && e.days <= lookAheadDays);
}

/**
 * Merge the two arms into ONE chronological list — the whole point of PR 2.4.
 *
 * SORTED HERE, NOT TRUSTED FROM EITHER SOURCE. The person arm arrives sorted
 * (the BE materializes `upcoming` by `nextEventAt` ascending), but the holiday
 * arm does NOT: `holidaysInRange` walks the `HOLIDAYS` array in declaration
 * order, which is mockup order, and the mockup is not a date sort (THE-746
 * item 8 — Lunar New Year sits before Valentine's, and in 2026 that is Feb 17
 * before Feb 14). Concatenating without sorting would show a correctly-ordered
 * list for most of the year and a wrong one in February.
 *
 * SAME-DAY TIE-BREAK: the person comes first. A holiday that lands on your
 * sister's birthday is context for it; the card you came to the page for leads.
 * Within one kind the input order is preserved (the comparator returns 0 and
 * `Array.prototype.sort` is stable per spec since ES2019), so the BE's
 * ordering of two events on one day survives untouched.
 */
export function mergeComingUpEntries(
  people: readonly ComingUpPersonEntry[],
  holidays: readonly ComingUpHolidayEntry[],
): ComingUpEntry[] {
  return [...people, ...holidays].sort((a, b) => {
    if (a.dayIso !== b.dayIso) return a.dayIso < b.dayIso ? -1 : 1;
    if (a.kind === b.kind) return 0;
    return a.kind === 'person' ? -1 : 1;
  });
}
